import { FunnelGrain } from '../enums';
import type { CompanySettings, SemaphoreThreshold } from '../schemas/companySettings';
import { DEFAULT_TIMEZONE } from '../constants';
import { dateKey, monthKey } from './dates';
import { isDigitalCategory } from './media';
import {
  appointmentDate,
  attributionCategory,
  isActiveSale,
  isActiveSeparation,
  isAppointmentConfirmed,
  isAppointmentGenerated,
  isCapture,
  isContact,
  isDigitalLead,
  isEffectiveContact,
  isSale,
  isSeparation,
  isTransitVisit,
  isValidVisit,
  realInteractions,
  saleDate,
  separationDate,
  visitDate,
} from './stages';
import type { FunnelConfig, FunnelDeal, FunnelInteraction, FunnelLead } from './types';
import {
  uniqueAppointmentIds,
  uniqueConfirmedAppointmentIds,
  uniqueLeadIds,
  uniqueVisitIds,
} from './uniqueness';

export const METRIC_KEYS = [
  'CAPTACIONES',
  'LEADS',
  'LEADS_UNICOS',
  'LEADS_RECONTACTO',
  'CONTACTOS_TOTAL',
  'CONTACTOS_EFECTIVOS',
  'CITAS_GENERADAS',
  'CITAS_CONCRETADAS',
  'VISITAS',
  'TRANSITO',
  'SEPARACIONES',
  'SEPARACIONES_ACTIVAS',
  'SEPARACIONES_DIGITALES',
  'VENTAS',
  'VENTAS_ACTIVAS',
  'VENTAS_DIGITALES',
] as const;
export type MetricKey = (typeof METRIC_KEYS)[number];
export type FunnelMetrics = Record<MetricKey, number>;

export const METRIC_LABELS: Record<MetricKey, string> = {
  CAPTACIONES: 'Captaciones',
  LEADS: 'Leads digitales',
  LEADS_UNICOS: 'Leads únicos',
  LEADS_RECONTACTO: 'Recontactos',
  CONTACTOS_TOTAL: 'Contactos',
  CONTACTOS_EFECTIVOS: 'Contactos efectivos',
  CITAS_GENERADAS: 'Citas generadas',
  CITAS_CONCRETADAS: 'Citas concretadas',
  VISITAS: 'Visitas',
  TRANSITO: 'Tránsito',
  SEPARACIONES: 'Separaciones',
  SEPARACIONES_ACTIVAS: 'Separaciones activas',
  SEPARACIONES_DIGITALES: 'Separaciones digitales',
  VENTAS: 'Ventas',
  VENTAS_ACTIVAS: 'Ventas activas',
  VENTAS_DIGITALES: 'Ventas digitales',
};

/** Etapas del embudo de reporte en orden, con la métrica que las representa. */
export const FUNNEL_STAGES: ReadonlyArray<{ key: MetricKey; label: string }> = [
  { key: 'CAPTACIONES', label: 'Captación' },
  { key: 'CONTACTOS_EFECTIVOS', label: 'Contacto efectivo' },
  { key: 'CITAS_GENERADAS', label: 'Cita generada' },
  { key: 'VISITAS', label: 'Visita' },
  { key: 'SEPARACIONES', label: 'Separación' },
  { key: 'VENTAS', label: 'Venta' },
];

export function emptyMetrics(): FunnelMetrics {
  return Object.fromEntries(METRIC_KEYS.map((k) => [k, 0])) as FunnelMetrics;
}

export function addMetrics(a: FunnelMetrics, b: FunnelMetrics): FunnelMetrics {
  const out = emptyMetrics();
  for (const k of METRIC_KEYS) out[k] = a[k] + b[k];
  return out;
}

export interface Dims {
  projectId: string;
  advisorId: string | null;
  mediumCategory: string | null;
}

/** Un hecho que cuenta en una métrica: la base auditable "fila por fila" del embudo. */
export interface FunnelEvent extends Dims {
  metric: MetricKey;
  date: Date;
  leadId: string;
  entityType: 'LEAD' | 'INTERACTION' | 'DEAL';
  entityId: string;
}

export interface DailyMetricRow extends Dims {
  date: string; // YYYY-MM-DD (Lima)
  metrics: FunnelMetrics;
}

export interface ComputeInput {
  leads: readonly FunnelLead[];
  interactions: readonly FunnelInteraction[];
  deals: readonly FunnelDeal[];
  config: FunnelConfig;
  grain: FunnelGrain;
  /** Mes 'YYYY-MM' (Lima) al que se limita el cálculo: cada etapa se imputa al mes de SU fecha. */
  month: string;
}

function dimsFor(grain: FunnelGrain, lead: FunnelLead, advisorId: string | null): Dims {
  const byAdvisor =
    grain === FunnelGrain.PROJECT_ADVISOR || grain === FunnelGrain.PROJECT_ADVISOR_CATEGORY;
  const byCategory =
    grain === FunnelGrain.PROJECT_CATEGORY || grain === FunnelGrain.PROJECT_ADVISOR_CATEGORY;
  return {
    projectId: lead.projectId,
    advisorId: byAdvisor ? advisorId : null,
    mediumCategory: byCategory ? (attributionCategory(lead) ?? 'SIN_MEDIO') : null,
  };
}

/**
 * Lista todos los hechos que cuentan en el mes al grano pedido. La unicidad (leads únicos, visitas únicas,
 * citas únicas) se calcula DENTRO de cada combinación de dimensiones del grano: por eso los totales por
 * medio no suman el total global (ver business_rules §6).
 */
export function collectFunnelEvents(input: ComputeInput): FunnelEvent[] {
  const { config, grain, month } = input;
  const tz = config.timezone ?? DEFAULT_TIMEZONE;
  const leads = input.leads.filter(isCapture);
  const leadById = new Map(leads.map((l) => [l.id, l]));
  const events: FunnelEvent[] = [];
  const push = (
    metric: MetricKey,
    date: Date,
    lead: FunnelLead,
    dims: Dims,
    entityType: FunnelEvent['entityType'],
    entityId: string,
  ) => {
    if (monthKey(date, tz) !== month) return;
    events.push({ metric, date, leadId: lead.id, entityType, entityId, ...dims });
  };

  // --- Captaciones y leads (fecha_registro) ---
  const groupKey = (l: FunnelLead) => {
    const d = dimsFor(grain, l, l.advisorId);
    return `${d.advisorId ?? ''}|${d.mediumCategory ?? ''}`;
  };
  const unique = uniqueLeadIds(leads, groupKey, tz);
  for (const lead of leads) {
    const dims = dimsFor(grain, lead, lead.advisorId);
    push('CAPTACIONES', lead.capturedAt, lead, dims, 'LEAD', lead.id);
    if (isDigitalLead(lead, config)) push('LEADS', lead.capturedAt, lead, dims, 'LEAD', lead.id);
    if (unique.has(lead.id)) push('LEADS_UNICOS', lead.capturedAt, lead, dims, 'LEAD', lead.id);
    else push('LEADS_RECONTACTO', lead.capturedAt, lead, dims, 'LEAD', lead.id);
  }

  // --- Interacciones (las simulaciones del bot no son hechos) ---
  const interactions = realInteractions(input.interactions).filter((i) => leadById.has(i.leadId));
  const uniqVisits = uniqueVisitIds(interactions, tz);
  const uniqAppts = uniqueAppointmentIds(interactions, tz);
  const uniqConfirmed = uniqueConfirmedAppointmentIds(interactions, tz);
  for (const i of interactions) {
    const lead = leadById.get(i.leadId)!;
    const dims = dimsFor(grain, lead, i.advisorId ?? lead.advisorId);
    if (isContact(i)) {
      push('CONTACTOS_TOTAL', i.completedAt!, lead, dims, 'INTERACTION', i.id);
      if (isEffectiveContact(i))
        push('CONTACTOS_EFECTIVOS', i.completedAt!, lead, dims, 'INTERACTION', i.id);
    }
    if (isAppointmentGenerated(i) && uniqAppts.has(i.id))
      push('CITAS_GENERADAS', appointmentDate(i)!, lead, dims, 'INTERACTION', i.id);
    if (isAppointmentConfirmed(i) && uniqConfirmed.has(i.id))
      push('CITAS_CONCRETADAS', i.visitAt!, lead, dims, 'INTERACTION', i.id);
    if (isValidVisit(i) && uniqVisits.has(i.id)) {
      push('VISITAS', visitDate(i)!, lead, dims, 'INTERACTION', i.id);
      if (isTransitVisit(i, lead, config))
        push('TRANSITO', visitDate(i)!, lead, dims, 'INTERACTION', i.id);
    }
  }

  // --- Procesos (sin dedupe) ---
  for (const deal of input.deals) {
    const lead = leadById.get(deal.leadId);
    if (!lead) continue;
    const dims = dimsFor(grain, lead, deal.advisorId ?? lead.advisorId);
    const digital = isDigitalCategory(attributionCategory(lead), config);
    if (isSeparation(deal, config)) {
      const d = separationDate(deal, config)!;
      push('SEPARACIONES', d, lead, dims, 'DEAL', deal.id);
      if (isActiveSeparation(deal, config))
        push('SEPARACIONES_ACTIVAS', d, lead, dims, 'DEAL', deal.id);
      if (digital) push('SEPARACIONES_DIGITALES', d, lead, dims, 'DEAL', deal.id);
    }
    if (isSale(deal, config)) {
      const d = saleDate(deal, config)!;
      push('VENTAS', d, lead, dims, 'DEAL', deal.id);
      if (isActiveSale(deal, config)) push('VENTAS_ACTIVAS', d, lead, dims, 'DEAL', deal.id);
      if (digital) push('VENTAS_DIGITALES', d, lead, dims, 'DEAL', deal.id);
    }
  }
  return events;
}

function rowKey(date: string, d: Dims): string {
  return `${date}|${d.projectId}|${d.advisorId ?? ''}|${d.mediumCategory ?? ''}`;
}

/** Agrega los eventos en filas diarias (lo que se persiste en funnel_daily_metrics). */
export function aggregateDaily(
  events: readonly FunnelEvent[],
  tz: string = DEFAULT_TIMEZONE,
): DailyMetricRow[] {
  const rows = new Map<string, DailyMetricRow>();
  for (const e of events) {
    const dk = dateKey(e.date, tz);
    const dims: Dims = {
      projectId: e.projectId,
      advisorId: e.advisorId,
      mediumCategory: e.mediumCategory,
    };
    const k = rowKey(dk, dims);
    let row = rows.get(k);
    if (!row) {
      row = { date: dk, ...dims, metrics: emptyMetrics() };
      rows.set(k, row);
    }
    row.metrics[e.metric] += 1;
  }
  return [...rows.values()].sort(
    (a, b) => a.date.localeCompare(b.date) || rowKey(a.date, a).localeCompare(rowKey(b.date, b)),
  );
}

/** Calcula las 16 métricas diarias de un mes al grano pedido. */
export function computeDailyMetrics(input: ComputeInput): DailyMetricRow[] {
  return aggregateDaily(collectFunnelEvents(input), input.config.timezone ?? DEFAULT_TIMEZONE);
}

/** Suma de filas diarias (válido dentro de un mismo grano). */
export function sumRows(rows: readonly DailyMetricRow[]): FunnelMetrics {
  return rows.reduce((acc, r) => addMetrics(acc, r.metrics), emptyMetrics());
}

export interface FunnelRatios {
  contactoEfectivo: number | null;
  recontacto: number | null;
  citasConcretadas: number | null;
  visitasSobreCaptaciones: number | null;
  separacionesSobreVisitas: number | null;
  ventasSobreCaptaciones: number | null;
}

function ratio(num: number, den: number): number | null {
  return den > 0 ? num / den : null;
}

export function computeRatios(m: FunnelMetrics): FunnelRatios {
  return {
    contactoEfectivo: ratio(m.CONTACTOS_EFECTIVOS, m.CONTACTOS_TOTAL),
    recontacto: ratio(m.LEADS_RECONTACTO, m.CAPTACIONES),
    citasConcretadas: ratio(m.CITAS_CONCRETADAS, m.CITAS_GENERADAS),
    visitasSobreCaptaciones: ratio(m.VISITAS, m.CAPTACIONES),
    separacionesSobreVisitas: ratio(m.SEPARACIONES, m.VISITAS),
    ventasSobreCaptaciones: ratio(m.VENTAS, m.CAPTACIONES),
  };
}

export interface StageConversion {
  from: MetricKey;
  to: MetricKey;
  fromValue: number;
  toValue: number;
  /** to / from, null si from = 0 */
  rate: number | null;
}

/** Conversión entre etapas consecutivas del embudo de reporte. */
export function stageConversions(m: FunnelMetrics): StageConversion[] {
  const out: StageConversion[] = [];
  for (let i = 1; i < FUNNEL_STAGES.length; i++) {
    const from = FUNNEL_STAGES[i - 1]!.key;
    const to = FUNNEL_STAGES[i]!.key;
    out.push({ from, to, fromValue: m[from], toValue: m[to], rate: ratio(m[to], m[from]) });
  }
  return out;
}

export type Semaphore = 'VERDE' | 'AMBAR' | 'ROJO' | 'SIN_DATOS';

export function semaphore(value: number | null, t: SemaphoreThreshold): Semaphore {
  if (value === null) return 'SIN_DATOS';
  if (value >= t.green) return 'VERDE';
  if (value >= t.amber) return 'AMBAR';
  return 'ROJO';
}

export function semaphores(
  m: FunnelMetrics,
  settings: Pick<CompanySettings, 'semaphore'>,
): Record<keyof FunnelRatios, Semaphore> {
  const r = computeRatios(m);
  return {
    contactoEfectivo: semaphore(r.contactoEfectivo, settings.semaphore.contactabilidad),
    recontacto:
      r.recontacto === null
        ? 'SIN_DATOS'
        : r.recontacto <= 0.2
          ? 'VERDE'
          : r.recontacto <= 0.35
            ? 'AMBAR'
            : 'ROJO',
    citasConcretadas: semaphore(r.citasConcretadas, settings.semaphore.citasConcretadas),
    visitasSobreCaptaciones: semaphore(
      r.visitasSobreCaptaciones,
      settings.semaphore.visitasSobreCaptaciones,
    ),
    separacionesSobreVisitas: semaphore(
      r.separacionesSobreVisitas,
      settings.semaphore.separacionesSobreVisitas,
    ),
    ventasSobreCaptaciones: semaphore(
      r.ventasSobreCaptaciones,
      settings.semaphore.ventasSobreCaptaciones,
    ),
  };
}

/** Avance real vs meta considerando el % del mes transcurrido. */
export interface TargetProgress {
  metric: MetricKey;
  real: number;
  target: number;
  /** real / meta */
  achieved: number | null;
  /** meta esperada a la fecha = meta × % mes transcurrido */
  expected: number;
  /** real / esperado */
  pace: number | null;
  status: Semaphore;
}

export function targetProgress(
  m: FunnelMetrics,
  targets: Partial<Record<MetricKey, number>>,
  elapsedRatio: number,
): TargetProgress[] {
  const out: TargetProgress[] = [];
  for (const [metric, target] of Object.entries(targets) as Array<[MetricKey, number]>) {
    if (!target) continue;
    const real = m[metric];
    const expected = target * elapsedRatio;
    const pace = ratio(real, expected);
    out.push({
      metric,
      real,
      target,
      achieved: ratio(real, target),
      expected: Math.round(expected * 10) / 10,
      pace,
      status:
        pace === null ? 'SIN_DATOS' : pace >= 0.95 ? 'VERDE' : pace >= 0.75 ? 'AMBAR' : 'ROJO',
    });
  }
  return out;
}

/** Tiempo de cierre en días (fecha venta − fecha separación). */
export function closingDays(separationAt: Date, saleAt: Date): number {
  return Math.round((saleAt.getTime() - separationAt.getTime()) / 86_400_000);
}
